import { AIFilter, selectedAIFilter } from './collection';
import { Istatus } from './onlineCancellation';
import { ClosetApiResponse, ClosetItem } from './closet';

export type IEtsDetailsObject = {
  orderByWindow: string;
  shipmentTime: string;
};

export type IQuoteObject = {
  originalPrice: string;
  taxAmount: string;
  taxPercent: string;
  totalAmount: string;
  discountedPriceWithTax: string;
};

export type DartContextValue = {
  dartContext: IDartContext;
  setDartContext: React.Dispatch<React.SetStateAction<IDartContext>>;
};

export type DartCollectionContainerProps = {
  isDartEnabled?: boolean;
  fireSysTimerStoppedGA: () => void;
  blockBodyScrollConfig: {
    isModalPresentInDOM: boolean;
    scrollBlockClass: string;
  };
  dbcCollection: DBCCollectionResponse;
  nbcCollection: NBCCollectionResponse;
  collectionSize: number;
  postDartOrder: (args: PostDartOrderArgs) => any;
  isPostDartOrderLoading: boolean;
  isNBCCollectionAvailable: boolean;
  openDartModal: (modalName: string) => void;
  closeDartModal: () => void;
  closeQCModal: () => void;
  openDartQCModal: () => void;
  isQCModalOpen: boolean;
};

export type IUpdateReservationsProp = {
  expiryTime: string;
  activeReservations: Array<ActiveReservationRes & {
    imageUrl?: string;
    size?: string;
    collectionType?: string;
  }>;
}

export type IDeleteAllReservationsResponse = {
  status: Istatus;
  reservations: {
    activeReservations: ActiveReservationRes[];
    expiryTime: string;
  };
}

export type IReservationIdMap = {
  [id: string]: string;
}

export type DBCCollectionSectionProps = {
  dbcCollection: DBCCollectionResponse;
  updateDBCReservedGAInfo: (arg: any) => IDBCSelectedProudctsGAInfo[];
  etsDetailsObject: any;
  collectionSize: number;
  blockBodyScrollConfig: {
    isModalPresentInDOM: boolean;
    scrollBlockClass: string;
  };
  reservationIdMap: IReservationIdMap;
  isQCModalOpen: boolean;
  closeQCModal: () => void;
  openDartQCModal: () => void;
  appliedFilters?: any;
  openDartModal: (modalName: string) => void;
  closeDartModal: () => void;
  handleOnClickFilterButton: () => void;
  expiryTime: string;
  dartCoachingBarCount: number;
  reservations: Reservations;
  availableSlots: number;
  postSingleReservation: Function;
  deleteSingleReservation: Function;
  etsDetails: {
    orderByWindow: string;
    shipmentTime: string;
  };
};
export type NbcCollectionArray = {
  id: number;
  size: string;
  sku: string;
  productInfo: {
    id: number;
    title: string;
    vendor: string;
    handle: string;
    type: string;
    images: [
      {
        position: string;
        type: string;
        url: string;
      }
    ];
    skus: Array<object>;
  };
};
export type NBCCollectionSectionProps = {
  nbcCollection: {
    collection: Array<NbcCollectionArray>;
  };
  collectionSize: number;
  openDartModal: (modalName: string) => void;
  closeDartModal: () => void;
  expiryTime: string;
  reservations: Reservations;
  availableSlots: number;
  postSingleReservation: Function;
  deleteSingleReservation: Function;
};

export type DartCollectionOptions = {
  onSuccess: (data: DartCollectionResponse) => void;
  onError: (err: unknown) => void;
  queryParams?: string;
  scope?: string;
  enabled: boolean;
};

export type DBCCollectionOptions = {
  queryParams: string;
  enabled: boolean;
};

export type DBCProduct = {
  brand: string;
  type?: string;
  dualSizeFilterMapping?: {
    [index: string]: any[];
  };
  numberOfReviews: number;
  avgRating: number;
  imgUrl: string;
  url: string;
  title: string;
  productType: string;
  handle: string;
  publishedAt: string;
  position: number;
  rtpAvailableSizes: Array<string>;
  availableVendorSizes: Array<string>;
  segments: Array<string>;
  season: string;
  color: string;
  seasonDetails: Array<string>;
  styleColors: Array<string>;
  featuredImgUrls: Array<string>;
  additionalImgUrls: Array<string>;
  gbCustomTags: Array<string>;
  productId: number;
  isReserved: boolean;
  isNBCOrderPlaced: boolean;
  reservedSku: string;
  reservationId: number;
};

type ImageType = {
  position: number;
  type: string;
  url: string;
};

type SkuType = {
  sku: string;
  position: number;
  available: boolean;
  size: string;
  color: string;
  style: string;
  variantSize: string;
  variantColor: string;
  variantStyle: string;
};

type ReservedProduct = {
  id: number;
  title: string;
  vendor: string;
  handle: string;
  type: string;
  images: ImageType[];
  skus: SkuType[];
  expiryTime: string;
  isReserved: boolean;
  isNBCOrderPlaced: boolean;
  reservedSku: string;
  reservationId: number;
};

export type DBCCollectionResponse = {
  totalProducts: number;
  totalProductsInCollection: number;
  returnedProducts: number;
  esQueryTime: string;
  absoluteEsQueryTime: string;
  collectionId: string;
  matchedProducts: DBCProduct[];
  availableColors: Array<string>;
  availableSizes: Array<string>;
  availableCSSizes: Array<string>;
  availableVendorSizes: Array<string>;
  availableBrands: Array<string>;
  availableRatings: Array<string>;
  filtersApplied: Array<string>;
  sortOrder: Array<string>;
  filteredSkus: Array<string>;
  eventInformation: string;
  availableDisplaySizes: Array<string>;
  allDisplaySizes: Array<string>;
  expiryTime: string;
  allBrands: Array<string>;
  allColors: Array<string>;
  allSizes: Array<string>;
  allCsSizes: Array<string>;
  reservedProducts: Array<ReservedProduct>;
  detailedFilters: Array<AIFilter>;
};
export type NBCCollectionOptions = {
  enabled: boolean;
};

export type NBCCollectionResponse = {
  collection: Array<NbcCollectionArray>;
};

export type DartCollectionResponse = {
  nbcCollection: NBCCollectionResponse;
  dbcCollection: DBCCollectionResponse;
  nbcDetails: NBCDetailsResponse;
  closetDetails: ClosetApiResponse
};

export type NBCDetailsOptions = {
  scope: string;
  enabled: boolean;
  onSuccess? : Function;
};

export type UserStatus = {
  availability: {
    featureAvailabilityWindow: string;
    isCollectionAvailable: boolean;
    isDBCCollectionAvailable: boolean;
    isOptIn: boolean;
    optOutTime: string;
    slots: number;
  };
  eligibility: {
    closetCount: number;
    closetMinCount: number;
    dfpTag: string;
    isUserEligible: boolean;
    numberOfSlots: number;
    error?: {
      code: number;
      message: number;
    };
  };
}

export type NBCDetailsResponse = {
  userStatus: UserStatus;
  reservations: Reservations;
  paymentInfo?: {
    creditCard: {
      cardType: string;
      last4Digits: string;
      source: string;
    };
  };
  quote?: {
    choices: QuoteChoices[];
    createdAt: string;
    namespaceId: number;
    signature: string;
    userUuid: string;
  };
  etsDetails: {
    orderByWindow: string;
    shipmentTime: string;
  };
  userIncentiveInfo: {
    incentiveDescription: string;
    incentiveId: number;
    isFreeDartEligible: boolean;
    userIncentiveId: number;
    incentiveName: string;
  };
};

export type NBCAndConfigDetailsOptions = {
  scope: string;
  enabled: boolean;
  isFetchNBCEnabled: boolean;
  isFetchConfigEnabled: boolean;
  onSuccess: (data: NBCAndConfigResponse) => void;
  onError: (err: unknown) => void;
};

export type NBCAndConfigResponse = {
  nbcData: NBCDetailsResponse;
  nbcConfig: {
    tenant: object;
  };
};

export type AddOrDeleteReservationOptions = {
  onSuccess: (data: AddOrDeleteReservationResponse) => void;
  onError: (err: unknown) => void;
};

type ActiveReservationRes = {
  id: number;
  userUuid: string;
  sku: string;
  expiryTime: string;
};

export type ActiveReservation = ActiveReservationRes & {
  imageUrl: string;
  size: string;
  collectionType: string;
};

export type IReservationsResponse = {
  status: Istatus;
  reservations: {
    activeReservations: ActiveReservationRes[];
    expiryTime: string;
  };
}

export type Reservations = {
  activeReservations: ActiveReservation[];
  expiryTime: string;
};

export type AddOrDeleteReservationResponse = {
  reservations: IReservationsResponse;
  source?: string;
  status: {
    code: number;
    message: string;
  };
};
export type postSingleReservationArgs = {
  sku: string;
  availableSlots: number;
  source: string;
};

export type DeleteSingleReservationOptions = {
  onSuccess: (data: AddOrDeleteReservationResponse) => void;
  onError: (err: unknown) => void;
};

export type DartWelcomePopupProps = {
  availableSlots: number;
  isNBCCollectionAvailable: boolean;
  handleOnCloseWelcomePopup: React.MouseEventHandler<HTMLButtonElement>;
};

export type DartConfirmationPopupProps = {
  reservations: Reservations;
  orderByWindowHours: string;
  ets: {
    day: string;
    numberOfDaysDiff: number;
    formattedDate: string;
  };
  postDartOrder: (args: PostDartOrderArgs) => any;
  fireSysTimerStoppedGA: () => void;
  isPostDartOrderLoading: boolean;
  openDartModal: (args: string) => void;
  closeDartModal: () => void;
};

export type DartReservedInfoSectionProps = {
  reservations: Reservations;
};

export type DartOrderSummarySectionProps = {
  quoteObject: IQuoteObject;
  isFreeDartEligible: boolean;
};

export type QuoteChoices = {
    id: number;
    type: string;
    originalPrice: number;
    discountExpiresOn: string;
    discountedPrice: number;
    schemeIncentive: string;
    description: string;
    tax: number;
    taxRate: string;
    taxAmount: number;
    discount: string;
    totalAmount: string;
    taxPercent: number;
    category: string;
    numItems: number;
    expiresOn: string;
    createdAt: string;
    updatedAt: string;
    userIncentiveId: string;
    userDiscountPriceWithoutTax: string;
    userDiscountPrice: string;
    totalAmountWithoutTax: string;
    discountedPriceWithTax: string;
    userDiscount: string;
};

export type DartOrderSectionProps = {
  expiryTime: string;
  reservations: Reservations['activeReservations'];
  checkWalleyPayButtonRendered: () => void;
  fireSysTimerStoppedGA: () => void;
  orderByWindowHours: string;
  handleCancelButtonClick: () => void;
  quote: {
    choices: QuoteChoices[];
  };
  quoteObject: IQuoteObject;
  paymentCardObject: {
    cardType: string;
    ccLast4Digits: string;
    isPaymentSourceWalletPay: boolean;
    paymentSource: string;
  };
  etsDetailsObject: {
    orderByWindow: string;
    shipmentTime: string;
  };
  ets: {
    day: string;
    numberOfDaysDiff: number;
    formattedDate: string;
  };
  isLoading: boolean;
  userIncentiveInfo: {
    isFreeDartEligible: boolean;
    incentiveName: string;
    incentiveDescription: string;
    userIncentiveId: number;
    incentiveId: number;
  };
  postDartOrder: Function;
  paymentNonce: any;
  optOutTime: string;
  dartOrderType: string;
  collectionSize: number;
};

export type PlaceDartOrderOptions = {
  onSuccess: (data: PlaceDartOrderResponse) => void;
  onError: (err: unknown) => void;
};

export type PlaceDartOrderResponse = {
    orderId: number;
    orderAttributes: [
        {
            attribute: string;
            value: boolean;
        },

    ];
    state: string;
    internalState: string;
    price: string;
    tax: string;
    paidAmount: string;
    paymentTransactionId: string;
};

export type PostDartOrderArgs = {
  sla: string;
  isRevisedSLA: boolean;
  skuList: string[];
  dbcSkuList: string[];
  quote: {
    choices: QuoteChoices[];
  };
  paymentToken?: string;
}
export type DartSingleGarmentContainerProps = {
  key: number;
  reservationInfo: any;
  expiryTime?: string;
  imgUrl: string;
  size?: string;
  sku?: string;
  brand?: string;
  title?: string;
  isReserved: boolean;
  isNBCCollection?: boolean;
  isDBCCollection?: boolean;
  productURL?: string;
  styleColors?: string[];
  dartCoachingBarCount?: number;
  postSingleReservation: Function;
  deleteSingleReservation: Function;
  handleOpen: (productInfo: { productId: number}) => void;
  productId: number;
  availableSlots?: number;
  reservationId: number;
  reservations: Reservations;
  collectionSize: number;
  openDartModal: (modalName: string) => void;
};

export const ORDER = {
  ASC: 'ASC',
  DESC: 'DESC'
};
export type DartSuccessPopupProps = {
  closerPageLink: string;
  isFreeDartEligible: boolean;
};

export type IdartFlowConfig = {
  isDartProduct: boolean;
  expiryTime: string;
  availableSlots: number;
  skuId: string;
  rtpAvailableSizes: string[];
  reservationInfo: Array<{skuId: string; reservationId: string}>;
  reservationIdMap: IReservationIdMap;
  coachingBarState?: any;
  timerProps: {
    expiryTime: string;
    reservationIdMap: IReservationIdMap;
    availableSlots: number;
    garmentLeft: number;
    garmentsPicked: number;
    handleTimerExpired: () => void;
  };
  dartModalConfig: any;
}

export type DartProductFlowURLConfig = {
  expiryTime: string;
  skuId: string;
  collectionSize: number;
  rtpAvailableSizes: string[];
  availableSlots: number;
  etsDetailsObject: {
    orderByWindow: string;
    shipmentTime: string;
  };
  reservationIdMap: IReservationIdMap;
  coachingBarState: {
    isFirstGarment: boolean;
    isLastGarment: boolean;
  };
  reservationInfo: {
    skuId: string;
    reservationId: number;
  }[];
};

export type DartModalConfig = {
  popupType: string;
  showModal: boolean;
  headingText: string;
  subHeadingText: string;
  ifFirstCta: boolean;
  ifSecondCta: boolean;
  showPopupContentText: boolean;
};

export type DartProductConfig = {
  isDartProduct: boolean;
  dartModalConfig: DartModalConfig;
  timerProps: {
    expiryTime: string;
    reservationIdMap: IReservationIdMap;
    availableSlots: number;
    garmentLeft: number;
    garmentsPicked: number;
  };
  expiryTime: string;
  reservationIdMap: IReservationIdMap;
  availableSlots: number;
  garmentLeft: number;
  garmentsPicked: number;
} & DartProductFlowURLConfig;

export type IUseDartProductPage = {
  dartFlowConfig: DartProductConfig;
  openDartModal: (modalName: string) => void;
  setdartFlowConfig: React.Dispatch<React.SetStateAction<DartProductConfig>>;
  handleTimerExpired: () => Promise<void>;
  handleDartModalClose: () => void;
  handleCloseReservationExpiredModal: () => void;
};

export type DartQCProductInfo = {
  brand: string;
  numberOfReviews: number;
  avgRating: number;
  imgUrl: string;
  url: string;
  title: string;
  productType: string;
  handle: string;
  publishedAt: string;
  position: number;
  rtpAvailableSizes: string[];
  availableVendorSizes: string[];
  segments: any[];
  season: string;
  color: string;
  seasonDetails: string[];
  styleColors: string[];
  featuredImgUrls: string[];
  additionalImgUrls: string[];
  gbCustomTags: any[];
  productId: number;
  isReserved: boolean;
  isNBCOrderPlaced: boolean;
  reservedSku: string;
  reservationId: number | null;
  carouselImagesCollection: string[];
  recommendedSku: string;
  advisedSize: string;
  selectedSize: string;
  isDualSizeFilterEnabled?: boolean;
  dualSizeFilterLengthKey?: string;
  dualSizeFilterFirstTab?: string;
  dualSizeFilterActiveTab?: string;
  dualSizeFilterMapping?: {
    [index: string]: any[];
  };
}

export interface DartQCModalState {
  isOpen: boolean;
  productInfo: DartQCProductInfo;
  dartButtonInfo: {
    isLoading: boolean;
    buttonText: string;
  };
  isSizeSelectorOpen: boolean;
  sizeAdvisorInfo: {
    recommendedSku: string;
    size: string;
    style: string;
  };
}

export type DartQCModalProps = {
  handleClickHideClosetSize: Function;
  handleClickDualSizeFilterTabs: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  QCModalState: DartQCModalState;
  handleClickShowClosetSize: Function;
  pushGAEventForSwipeImageLeft: (swiper: any) => void;
  pushGAEventForSwipeImageRight: (swiper: any) => void;
  handleSizeBubbleClicked: (size: string) => void;
  handleViewProductDetailsClick: (e: any) => void;
  handleViewReviewsClick: () => void;
  addToDartClicked: () => void;
};

export type ICoachingBarContainerProps = {
  expiryTime: string;
  showSuccessPopup: boolean;
  showConfirmationPopup: boolean;
  openDartModal: (modalName: string) => void;
  availableSlots: number;
  garmentLeft: number;
  garmentsPicked: number;
  handleTimerExpired: () => Promise<void>;
  fireCoachingBarSysGAEvent: () => void;
  children?: React.ReactNode;
}

export type ICoachingBarState = {
  userClosetCount: number;
  coachingBarSuccess: boolean;
  timeToBeShow: string;
  remainingSlots: number;
  closetCountRequired: number;
  dartWindowRemainingSeconds: number;
  displayCoachingPopup: boolean;
  coachingBarClass: string;
  coachingPopupBtnClass: string;
  coachingBarMessage: string;
}

export type IDBCSelectedProudctsGAInfo = {
  status: string;
  isQC: boolean;
  saUsed: boolean;
  brand: string;
  sku: string;
  productHandle: string;
  collectionHandle: string;
  isSegmentedCollection: boolean;
  recommendedSize: string;
  wasRecommendedSizeSelected: string;
  selectedSize: string;
  recAlgoName: string;
  fromRec: boolean;
  recSourcePage: boolean;
}

export type IDartContext = {
  dbcCollection: DBCCollectionResponse;
  dbcSelectedProudctsGAInfo: IDBCSelectedProudctsGAInfo[];
  nbcCollection: NBCCollectionResponse;
  reservations: Reservations;
  isQCModalOpen: boolean;
  dartModalConfig: {
    showModal: boolean;
    popupType: string;
    newEtsDay: string;
    ifFirstCta: boolean;
    ifSecondCta: boolean;
    showPopupContentText: boolean;
  };
  // TODO: review its usage
  etsDetailFromOrderService?: any;
  nbcConfig: {
    'feature.availability.window.minutes': number;
    'reservation.expiry.time.window.minutes': number;
    'collection.refresh.timer.minutes': number;
  };
  reservationIdMap: IReservationIdMap;
  isNBCCollectionAvailable: boolean;
  isDBCCollectionAvailable: boolean;
  expiryTime: string;
  skuFilterConfig: {
    isFilterListUpdating: boolean;
    isFilterDrawerVisible: boolean;
    activeFilterIndex: number | null;
    appliedFilters: { [index: string]: string[]};
    selectedFilters: { [index: string]: string[]};
  };
  availableSlots: number;
  optOutTime: string;
  etsTime: string;
  orderByWindow: string;
  dartCoachingBarCount: number;
  showDartConfirmation: boolean;
  showDartSuccessScreen: boolean;
  isUserDartEligible: boolean;
  isDartEnabled: boolean;
  isPartialShipmentAllowed: boolean;
  isUkTenant: boolean;
  isFreeDartEnabled: boolean;
  isFreeDartEligible: boolean;
  showLoader: boolean;
  isError: boolean;
  dartErrorCode: number;
  isUserReturnedToDartFromProduct: boolean;
  initiateConfirmationPopupLoad: boolean;
  showConfirmationPopup: boolean;
  showSuccessPopup: boolean;
  closetData: ClosetApiResponse;
  quote: {
    choices: QuoteChoices[];
    createdAt: string;
    namespaceId: number;
    signature: string;
    userUuid: string;
  };
  quoteObject: {
    originalPrice: string;
    taxAmount: string;
    taxPercent: string;
    totalAmount: string;
    discountedPriceWithTax: string;
  };
  paymentCardObject: {
    cardType: string;
    ccLast4Digits: string;
    isPaymentSourceWalletPay: boolean;
    paymentSource: string;
  };
  etsDetailsObject: IEtsDetailsObject;
  userIncentiveInfoObject: {
    isFreeDartEligible: boolean;
    incentiveName: string;
    incentiveDescription: string;
    userIncentiveId: number;
    incentiveId: number;
  };
  dartAvailablityConfig: {
    isUserEligible: boolean;
    errorType: string;
  };
}

export type IOpenDartModalParams = {
  dartContext: IDartContext;
  setDartContext: React.Dispatch<React.SetStateAction<IDartContext>>;
  modalName: string;
}

export type FetchDBCProductInfoOptions = {
  enabled: boolean;
  productHandle: string;
}

export type DBCProductResponse = {
  dualSizeFilterMapping?: {
    [index: string]: any[];
  };
  handle: string;
  rtpAvailableSkus: [{
    closetable: boolean;
    inventoryAvailable: boolean;
    position: number;
    retired: boolean;
    sku: string;
    variantSize: string;
  }];
}

export type QueryBuilderOptions = {
  limit: number | string;
  startIndex: number | string;
  appliedFilters: selectedAIFilter;
};

